/**
 * RunHistoryRail.tsx — the quiet right column of the App Integrity page
 * (UI v2): the append-only run history (each run keeps its own Formula
 * Verification PDF) and the Automation summary (schedule state at a glance,
 * full settings behind one click).
 */
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { PictureAsPdf, ScheduleOutlined, SettingsOutlined } from '@mui/icons-material';
import type { IntegrityRun, IntegrityScheduleSettings } from '../../../services/appIntegrityService';
import { statusChipColor } from './integrityDisplay';

interface RunHistoryRailProps {
  runs: IntegrityRun[];
  selectedRunKey: string | null;
  downloadingKey: string | null;
  schedule: IntegrityScheduleSettings | null;
  onSelectRun: (runKey: string) => void;
  onDownloadPdf: (runKey: string) => void;
  onOpenScheduling: () => void;
}

export const RunHistoryRail = ({
  runs,
  selectedRunKey,
  downloadingKey,
  schedule,
  onSelectRun,
  onDownloadPdf,
  onOpenScheduling,
}: RunHistoryRailProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {/* ── Automation ────────────────────────────────────────────────── */}
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleOutlined fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Automation
        </Typography>
        <Button size="small" startIcon={<SettingsOutlined />} onClick={onOpenScheduling}>
          Configure
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {schedule === null
          ? 'Loading…'
          : schedule.enabled
            ? `Runs ${schedule.cadence === 'weekly' ? 'weekly' : 'monthly'} · email ONLY on failure` +
              `${schedule.recipients.length ? ` to ${schedule.recipients.length} recipient(s)` : ' — no recipients yet'}. ` +
              'A watchdog emails if a due run never happens, so silence means "ran and passed".'
            : 'Scheduled runs are OFF. Turn them on to run the suite automatically and email only when something fails.'}
      </Typography>
      {schedule?.enabled && schedule.nextDueAt && (
        <Typography variant="caption" color="text.secondary">
          Next due: {new Date(schedule.nextDueAt).toLocaleString()}
        </Typography>
      )}
    </Paper>

    {/* ── Run history ───────────────────────────────────────────────── */}
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 1.75, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Run history
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Every run is kept — select one to view it in the table; ƒ downloads its PDF
        </Typography>
      </Box>
      <List dense disablePadding sx={{ maxHeight: '52vh', overflow: 'auto' }}>
        {runs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No runs yet.
          </Typography>
        )}
        {runs.map((run) => (
          <ListItemButton
            key={run.IntegrityRunKey}
            selected={run.IntegrityRunKey === selectedRunKey}
            onClick={() => onSelectRun(run.IntegrityRunKey)}
            sx={{ gap: 1, py: 0.75, borderBottom: 1, borderColor: 'divider', alignItems: 'flex-start' }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip
                  size="small"
                  color={statusChipColor(run.Status)}
                  label={run.Status.toUpperCase()}
                  sx={{ height: 18, fontSize: '0.62rem' }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                  {new Date(run.StartedAt).toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {run.PassCount}/{run.FixtureCount} passed · {run.AppVersion} · {run.TriggeredBy}
              </Typography>
            </Box>
            {run.Status !== 'running' && (
              <Tooltip title="Download this run's Formula Verification PDF">
                <span>
                  <IconButton
                    size="small"
                    disabled={downloadingKey !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownloadPdf(run.IntegrityRunKey);
                    }}
                  >
                    {downloadingKey === run.IntegrityRunKey
                      ? <CircularProgress size={14} />
                      : <PictureAsPdf sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </ListItemButton>
        ))}
      </List>
    </Paper>
  </Box>
);
